import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { RootState, Subtask } from 'constants/interfaces'
import { AppDispatch } from 'config/redux'
import { getAllResults } from 'redux/actions/tasks_v2'
import { findResultForMutation, resolveResultOccurrence } from '../../model/resultRequests'
import { fetchCourseProgress } from '../api/courseProgress'
import {
  courseProgressRequest,
  ICourseProgress,
  isCourseFinished,
  courseState,
  isCourseLink,
} from '../model/courseProgress'
import { isResultCompleted } from '../model/results'
import { useTaskOverviewContext } from '../TaskOverviewStackProvider'

/**
 * The executor's saved SCORM progress for a course link subtask. Fetched when the subtask's panel opens
 * and again when the learner returns to this window from the course tab. Only course links in execution
 * mode are fetched; every other subtask makes no request.
 */
export function useCourseProgress(subtask: Subtask, paused = false) {
  const { task, resultContextId, isReadMode } = useTaskOverviewContext()
  const dispatch: AppDispatch = useDispatch()
  const store = useStore<RootState>()
  const rid = useSelector((state: RootState) => state.config.config.rid)

  const [progress, setProgress] = useState<ICourseProgress | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [failed, setFailed] = useState(false)

  // Each request gets a number; only the latest one, for the current scope, may update state.
  const requestRef = useRef(0)
  const pausedRef = useRef(paused)

  const enabled = isCourseLink(subtask) && !isReadMode && !!rid
  const request = enabled ? courseProgressRequest(subtask, task, resultContextId, rid) : null
  const scope = JSON.stringify(request)

  const load = useCallback(async () => {
    if (!request) return

    const id = ++requestRef.current
    setIsLoading(true)

    try {
      const loaded = await fetchCourseProgress(request)
      if (id !== requestRef.current) return

      setProgress(loaded)
      setFailed(false)

      // The server marked the subtask DONE: refresh its result so the overview shows it without a reload.
      const result = findResultForMutation(
        store.getState().tasks_v2.allResults[subtask.tid],
        subtask.st_id,
        resultContextId,
        resolveResultOccurrence(task.recurrence_ts)
      )
      if (loaded?.result_reported_at_ts && isCourseFinished(courseState(loaded)) && !isResultCompleted(result)) {
        dispatch(getAllResults({ task, subtasksIds: [subtask.st_id] }))
      }
    } catch {
      // Without progress the panel looks as it did before this feature; the next return retries.
      if (id === requestRef.current) setFailed(true)
    } finally {
      if (id === requestRef.current) setIsLoading(false)
    }
    // `scope` stands for the request fields; the task object is only read when the request resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, dispatch, store])

  useEffect(() => {
    pausedRef.current = paused
  }, [paused])

  useEffect(() => {
    setProgress(null)
    setFailed(false)
    setIsLoading(false)
    if (!enabled) return

    // returning from the course tab; while the in-task viewer is open (paused), focus moves between the
    // course iframe and the page all the time, which is not a return (the viewer reloads on close)
    const handleReturn = () => {
      if (document.visibilityState === 'visible' && !pausedRef.current) load()
    }

    load()
    window.addEventListener('focus', handleReturn)
    document.addEventListener('visibilitychange', handleReturn)

    return () => {
      // A response for the previous scope must not land on the next subtask.
      requestRef.current += 1
      window.removeEventListener('focus', handleReturn)
      document.removeEventListener('visibilitychange', handleReturn)
    }
  }, [enabled, load])

  // reload: e.g. right after the course viewer closes
  return { enabled, progress, isLoading, failed, reload: load }
}
